import React from 'react';
import { Shield, Lock, Eye, Database, Mail, UserCheck, Globe, FileText } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const PrivacyPolicyPage = () => {
  const { t } = useLanguage();

  const sections = [
    {
      icon: Database,
      title: 'Dati Raccolti',
      content: `Raccogliamo i seguenti dati personali quando utilizzi QANTRI:
        • Informazioni di contatto: nome, cognome, indirizzo email, numero di telefono
        • Informazioni di spedizione: indirizzo di consegna, città, CAP, paese
        • Informazioni di pagamento: i dati di pagamento sono gestiti direttamente da PayPal
        • Dati di navigazione: indirizzo IP, tipo di browser, pagine visitate
        • Dati aziendali (opzionali): ragione sociale, partita IVA, codice SDI, PEC`
    },
    {
      icon: Eye,
      title: 'Come Utilizziamo i Tuoi Dati',
      content: `I tuoi dati vengono utilizzati per:
        • Processare e spedire i tuoi ordini
        • Comunicare aggiornamenti sullo stato degli ordini
        • Fornire assistenza clienti
        • Emettere fatture (se richieste)
        • Migliorare la tua esperienza di navigazione
        • Inviarti promozioni (solo con il tuo consenso)
        • Gestire il programma affiliati e referral`
    },
    {
      icon: Lock,
      title: 'Sicurezza dei Dati',
      content: `La sicurezza dei tuoi dati è la nostra priorità:
        • Utilizziamo crittografia SSL/TLS per tutte le comunicazioni
        • I pagamenti sono gestiti tramite PayPal, leader nella sicurezza online
        • Non conserviamo dati delle carte di credito sui nostri server
        • Accesso ai dati limitato solo al personale autorizzato
        • Backup regolari e misure di protezione contro accessi non autorizzati`
    },
    {
      icon: UserCheck,
      title: 'I Tuoi Diritti (GDPR)',
      content: `In conformità al GDPR, hai diritto a:
        • Accesso: richiedere una copia dei tuoi dati personali
        • Rettifica: correggere dati inesatti o incompleti
        • Cancellazione: richiedere l'eliminazione dei tuoi dati ("diritto all'oblio")
        • Portabilità: ricevere i tuoi dati in formato digitale
        • Opposizione: opporti al trattamento per finalità di marketing
        • Limitazione: limitare il trattamento in determinate circostanze`
    },
    {
      icon: Globe,
      title: 'Cookie e Tecnologie di Tracciamento',
      content: `Utilizziamo cookie per:
        • Cookie tecnici: necessari per il funzionamento del sito
        • Cookie di sessione: per mantenere il carrello e l'accesso
        • Cookie analitici: per comprendere come utilizzi il sito (anonimizzati)
        
        Puoi gestire le preferenze sui cookie attraverso le impostazioni del tuo browser.`
    },
    {
      icon: Mail,
      title: 'Comunicazioni',
      content: `Ti contatteremo solo per:
        • Conferme d'ordine e aggiornamenti sulla spedizione
        • Risposte alle tue richieste di assistenza
        • Newsletter (solo se ti sei iscritto, puoi disiscriverti in qualsiasi momento)
        • Comunicazioni importanti relative al tuo account`
    },
    {
      icon: FileText,
      title: 'Condivisione dei Dati',
      content: `I tuoi dati possono essere condivisi solo con:
        • Corrieri per la consegna degli ordini
        • PayPal per l'elaborazione dei pagamenti
        • Fornitori IT per la manutenzione del sito
        • Autorità competenti se richiesto dalla legge
        
        Non vendiamo mai i tuoi dati a terze parti per scopi di marketing.`
    }
  ];

  return (
    <div className="min-h-screen py-12 md:py-16" data-testid="privacy-policy-page">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#064E3B]/10 mb-6">
            <Shield className="w-8 h-8 text-[#064E3B]" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold font-serif mb-4">
            Privacy Policy
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            La tua privacy è importante per noi. Questa pagina descrive come raccogliamo, 
            utilizziamo e proteggiamo i tuoi dati personali quando utilizzi QANTRI.
          </p>
          <p className="text-sm text-muted-foreground mt-4">
            Ultimo aggiornamento: Dicembre 2025
          </p>
        </div>

        {/* Introduction */}
        <div className="bg-[#064E3B]/5 rounded-2xl p-6 md:p-8 mb-8">
          <h2 className="text-xl font-bold font-serif mb-4">Titolare del Trattamento</h2>
          <p className="text-muted-foreground leading-relaxed">
            QANTRI è il titolare del trattamento dei dati personali raccolti attraverso questo sito web.
            Ci impegniamo a rispettare il Regolamento Generale sulla Protezione dei Dati (GDPR - UE 2016/679)
            e tutte le normative italiane ed europee in materia di privacy.
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-6">
          {sections.map((section, index) => (
            <div 
              key={index}
              className="bg-white rounded-2xl border border-border p-6 md:p-8 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start gap-4">
                <div className="shrink-0 w-12 h-12 rounded-xl bg-[#064E3B]/10 flex items-center justify-center">
                  <section.icon className="w-6 h-6 text-[#064E3B]" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold font-serif mb-3">{section.title}</h3>
                  <div className="text-muted-foreground leading-relaxed whitespace-pre-line">
                    {section.content}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Retention Period */}
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 md:p-8 mt-8">
          <h3 className="text-lg font-bold font-serif mb-3 text-amber-800">Periodo di Conservazione</h3>
          <p className="text-amber-700 leading-relaxed">
            I tuoi dati personali saranno conservati per il tempo strettamente necessario 
            a soddisfare le finalità per cui sono stati raccolti, e comunque non oltre i 
            termini previsti dalla legge (es. 10 anni per documenti fiscali). 
            I dati di navigazione vengono eliminati automaticamente dopo 24 mesi.
          </p>
        </div>

        {/* Contact */}
        <div className="bg-white rounded-2xl border border-border p-6 md:p-8 mt-8">
          <h3 className="text-lg font-bold font-serif mb-3">Contattaci</h3>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Per qualsiasi domanda riguardante questa Privacy Policy o per esercitare i tuoi diritti, 
            puoi contattarci attraverso:
          </p>
          <ul className="space-y-2 text-muted-foreground">
            <li className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#064E3B]" />
              <span>Email: privacy@qantri.store</span>
            </li>
            <li className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-[#064E3B]" />
              <span>Pagina di supporto: <a href="/support" className="text-[#064E3B] hover:underline">/support</a></span>
            </li>
          </ul>
        </div>

        {/* Footer Note */}
        <p className="text-center text-sm text-muted-foreground mt-8">
          Questa Privacy Policy può essere aggiornata periodicamente. 
          Ti invitiamo a consultarla regolarmente per essere informato su come proteggiamo i tuoi dati.
        </p>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
